import React from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { HeartIcon, ClockIcon } from '@heroicons/react/24/outline';

interface PersonalBestCardProps {
  mode: 'survival' | 'rush';
  duration?: number;
  score: number;
  date?: string;
  gameLink: string;
}

const PersonalBestCard: React.FC<PersonalBestCardProps> = ({
  mode,
  duration,
  score,
  date,
  gameLink
}) => {
  const isRecent = date && new Date(date) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

  const getModeColor = () => {
    if (mode === 'survival') {
      return {
        bg: 'bg-red-50 dark:bg-red-900/20',
        border: 'border-red-200 dark:border-red-800',
        iconBg: 'bg-red-100 dark:bg-red-900',
        iconColor: 'text-red-600 dark:text-red-400',
        scoreColor: 'text-red-600 dark:text-red-400',
      };
    }
    return {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      iconBg: 'bg-blue-100 dark:bg-blue-900',
      iconColor: 'text-blue-600 dark:text-blue-400',
      scoreColor: 'text-blue-600 dark:text-blue-400',
    };
  };

  const getIcon = () => {
    if (mode === 'survival') {
      return <HeartIcon className="h-5 w-5" />;
    }
    return <ClockIcon className="h-5 w-5" />;
  };

  const getTitle = () => {
    if (mode === 'survival') return 'Survival';
    return `Rush ${duration}min`;
  };

  const getLabel = () => {
    if (mode === 'survival') return 'Best Streak';
    return 'Best Score';
  };

  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return new Date(dateString).toLocaleDateString();
    }
  };

  const colors = getModeColor();

  return (
    <Link
      to={gameLink}
      className={`block ${colors.bg} border ${colors.border} rounded-lg p-4 transition-all hover:shadow-lg hover:scale-105 ${
        isRecent ? 'ring-2 ring-yellow-400 ring-opacity-50 shadow-yellow-100' : ''
      }`}
    >
      <div className="flex items-center mb-2">
        <div className={`p-1 ${colors.iconBg} rounded`}>
          <div className={colors.iconColor}>
            {getIcon()}
          </div>
        </div>
        <h3 className="ml-2 font-medium text-gray-900 dark:text-white text-sm">
          {getTitle()}
        </h3>
      </div>
      <div className="text-center">
        <p className={`text-2xl font-bold ${colors.scoreColor}`}>
          {score}
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {getLabel()}
        </p>
        {date && (
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
            {formatDate(date)}
          </p>
        )}
        {isRecent && (
          <p className="text-xs font-medium text-yellow-600 dark:text-yellow-400 mt-1">
            🔥 New!
          </p>
        )}
      </div>
    </Link>
  );
};

export default PersonalBestCard;
